/** V2 canvas host retained as the stable shell integration boundary. */
import type { Viewport, WordOffset, LetterOffset, DotOffset } from '@/types'
import { V2Canvas } from '@/v2/components/V2Canvas'
import type { V2Runtime } from '@/v2/runtime/V2Runtime'
import styles from './Canvas.module.css'

interface CanvasWrapperProps {
  v2Runtime?: V2Runtime | null
  renderingEnabled: boolean
  onResize: (width: number, height: number) => void
  onViewportChange: (viewport: Partial<Viewport>) => void
  // Word mode callbacks
  onWordDrag: (wordIndex: number, offset: Partial<WordOffset>) => void
  onWordDragBatch: (updates: Array<{ index: number; offset: Partial<WordOffset> }>) => void
  onWordDragEnd: () => void
  onSelectWord: (index: number | null) => void
  // Letter mode callbacks
  onLetterDrag: (letterIndex: number, offset: Partial<LetterOffset>) => void
  onLetterDragBatch: (updates: Array<{ index: number; offset: Partial<LetterOffset> }>) => void
  onLetterDragEnd: () => void
  onSelectLetter: (index: number | null) => void
  // Dot mode callbacks
  onDotDrag: (dotIndex: number, offset: Partial<DotOffset>) => void
  onDotDragEnd: () => void
  onSelectDot: (index: number | null) => void
}

export function CanvasWrapper({
  v2Runtime,
  renderingEnabled,
  onResize,
  onViewportChange,
  onWordDrag,
  onWordDragBatch,
  onWordDragEnd,
  onSelectWord,
  onLetterDrag,
  onLetterDragBatch,
  onLetterDragEnd,
  onSelectLetter,
  onDotDrag,
  onDotDragEnd,
  onSelectDot,
}: CanvasWrapperProps) {
  if (v2Runtime) {
    return (
      <V2Canvas
        runtime={v2Runtime}
        renderingEnabled={renderingEnabled}
        onResize={onResize}
        onViewportChange={onViewportChange}
        onWordDrag={onWordDrag}
        onWordDragBatch={onWordDragBatch}
        onWordDragEnd={onWordDragEnd}
        onSelectWord={onSelectWord}
        onLetterDrag={onLetterDrag}
        onLetterDragBatch={onLetterDragBatch}
        onLetterDragEnd={onLetterDragEnd}
        onSelectLetter={onSelectLetter}
        onDotDrag={onDotDrag}
        onDotDragEnd={onDotDragEnd}
        onSelectDot={onSelectDot}
      />
    )
  }

  return (
    <div
      className={styles.canvas}
      data-v2-renderer="pending"
      data-v2-runtime-status="pending"
      role="status"
      aria-label="Renderer is preparing"
    />
  )
}
